import React, { useEffect, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { ArrowUpRight, LoaderCircle, Megaphone, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { parse } from 'psl';
import './ImportantNotice.css';
import { generateAIFleetTokenLink } from '../../utils/generateTokenLink';

// 判断真实访问域名，不使用鉴权跳转的域名兜底；本地环境也可预览通知。
export const shouldShowImportantNotice = (hostname) => {
  const host = typeof hostname === 'string' ? hostname.trim().toLowerCase().replace(/\.$/, '') : '';
  const parsed = parse(host);
  return host !== 'zd87.com' && parsed.domain !== 'zd87.com';
};

const ImportantNotice = ({ isLoggedIn = false, userToken }) => {
  const { t } = useTranslation();
  const dialogRef = useRef(null);
  const confirmRef = useRef(null);
  const dismissedRef = useRef(false);
  const navigatingRef = useRef(false);
  const [navigating, setNavigating] = useState(false);

  useEffect(() => {
    const resetNavigation = () => {
      navigatingRef.current = false;
      setNavigating(false);
    };
    window.addEventListener('pageshow', resetNavigation);
    // 导航未完成或被浏览器取消时，允许用户再次点击。
    const timer = navigating ? window.setTimeout(resetNavigation, 15000) : null;
    return () => {
      window.removeEventListener('pageshow', resetNavigation);
      window.clearTimeout(timer);
    };
  }, [navigating]);
  const visible = typeof window !== 'undefined' && shouldShowImportantNotice(window.location.hostname);

  useEffect(() => {
    if (!visible || dismissedRef.current) return undefined;
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    confirmRef.current.focus({ preventScroll: true });
    document.body.style.overflow = 'hidden';
    const onClose = () => {
      if (dialog.open) return;
      dismissedRef.current = true;
      document.body.style.overflow = previousOverflow;
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    };
    dialog.addEventListener('close', onClose);
    return () => {
      dialog.removeEventListener('close', onClose);
      dialog.close();
      document.body.style.overflow = previousOverflow;
    };
  }, [visible]);

  if (!visible) return null;
  const close = () => dialogRef.current.close();
  const directUrl = isLoggedIn && typeof userToken === 'string' && userToken.trim()
    ? generateAIFleetTokenLink(userToken, 'https://zd87.com') : null;
  const destination = isLoggedIn ? directUrl : 'https://zd87.com/';
  const handleNavigate = (event) => {
    if (navigatingRef.current || !destination) {
      event.preventDefault();
      return;
    }
    // 保留浏览器 Ctrl/Cmd 点击新标签页的原生行为。
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
    navigatingRef.current = true;
    flushSync(() => setNavigating(true));
  };
  const navigationIcon = navigating
    ? <LoaderCircle size={16} className="important-notice-spinner" aria-hidden="true" />
    : <ArrowUpRight size={16} aria-hidden="true" />;
  return createPortal(
    <dialog ref={dialogRef} className="important-notice" aria-labelledby="important-notice-title" aria-describedby="important-notice-content">
      <div className="important-notice-heading">
        <div className="important-notice-icon"><Megaphone size={24} aria-hidden="true" /></div>
        <h2 id="important-notice-title">{t('importantNotice.title')}</h2>
        <button type="button" className="important-notice-close" onClick={close} aria-label={t('importantNotice.close')}><X size={20} /></button>
      </div>
      <div className="important-notice-body" id="important-notice-content">
        <section className="important-notice-section" aria-labelledby="notice-address-title">
          <h3 id="notice-address-title"><span aria-hidden="true">01</span>{t('importantNotice.addressTitle')}</h3>
          <p>{t('importantNotice.content')}</p>
          <div className="important-notice-address">
            <span>{t('importantNotice.addressLabel')}</span>
            <strong>zd87.com</strong>
            {isLoggedIn && (directUrl ? (
              <a className="important-notice-confirm important-notice-direct" href={directUrl} referrerPolicy="no-referrer" onClick={handleNavigate} aria-busy={navigating} aria-disabled={navigating}>
                {t(navigating ? 'importantNotice.navigating' : 'importantNotice.directVisit')}{navigationIcon}
              </a>
            ) : (
              <div>
                <button type="button" className="important-notice-confirm important-notice-direct" disabled>{t('importantNotice.directVisit')}</button>
                <p className="important-notice-hint">{t('importantNotice.loginAgain')}</p>
              </div>
            ))}
          </div>
          <p className="important-notice-hint">{t('importantNotice.bookmark')}</p>
        </section>
        <section className="important-notice-section" aria-labelledby="notice-fleet-title">
          <h3 id="notice-fleet-title"><span aria-hidden="true">02</span>{t('importantNotice.fleetTitle')}</h3>
          <div className="important-notice-pool important-notice-pool-legacy">
            <div className="important-notice-pool-heading">
              <h4>{t('importantNotice.legacyTitle')}</h4>
              <strong className="important-notice-deadline">{t('importantNotice.deadline')}</strong>
            </div>
            <p>{t('importantNotice.legacyDescription')}</p>
            <p className="important-notice-backup">{t('importantNotice.backupReminder')}</p>
          </div>
          <div className="important-notice-pool important-notice-pool-plus">
            <h4>{t('importantNotice.plusTitle')}</h4>
            <p>{t('importantNotice.plusDescription')}</p>
          </div>
        </section>
      </div>
      <div className="important-notice-footer">
        <a className="important-notice-confirm" ref={confirmRef} href={destination || undefined} referrerPolicy="no-referrer" onClick={handleNavigate} aria-busy={navigating} aria-disabled={navigating || !destination}>{t(navigating ? 'importantNotice.navigating' : 'importantNotice.visit')}{navigationIcon}</a>
        <span className="sr-only" role="status">{navigating ? t('importantNotice.navigating') : ''}</span>
        <button type="button" className="important-notice-stay" onClick={close}>{t('importantNotice.confirm')}</button>
      </div>
    </dialog>, document.body
  );
};

export default ImportantNotice;
